import { useState, useEffect, useMemo, useCallback } from 'react';
import { useFieldVisibility, VisibilityContext } from '@/context/FieldVisibilityContext';
import { useTable } from '@/context/TableContext';
import { fetchTableColumns, getTableFieldVisibilityConfig, updateTableFieldVisibilityConfig, FieldVisibilityConfig } from '@/services/supabase';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { Settings, RotateCcw, Check, Database, Loader2 } from 'lucide-react';

interface FieldVisibilitySettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const CONTEXTS: { key: VisibilityContext; label: string; description: string }[] = [
  { key: 'table', label: 'Table', description: 'Dashboard data table columns' },
  { key: 'modal', label: 'Modal', description: 'Record details popup' },
  { key: 'csv', label: 'CSV', description: 'CSV export file' },
  { key: 'pdf', label: 'PDF', description: 'PDF export file' },
];

// Default visibility for dynamic fields
const DEFAULT_FIELD_VISIBILITY = {
  table: true,
  modal: true,
  csv: true,
  pdf: true,
};

export function FieldVisibilitySettings({ isOpen, onClose }: FieldVisibilitySettingsProps) {
  const { updateFieldVisibilityBatch, tableName: contextTableName } = useFieldVisibility();
  const { availableTables } = useTable();

  const [, setHasChanges] = useState(false);
  const [localConfig, setLocalConfig] = useState<Record<string, Record<VisibilityContext, boolean>>>({});
  const [selectedTable, setSelectedTable] = useState(contextTableName);
  const [tableColumns, setTableColumns] = useState<string[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);

  // Convert database config to local config format
  const convertToLocalConfig = useCallback((dbConfig: FieldVisibilityConfig | null): Record<string, Record<VisibilityContext, boolean>> => {
    if (!dbConfig) return {};
    
    const config: Record<string, Record<VisibilityContext, boolean>> = {};
    Object.keys(dbConfig).forEach((key) => {
      const contextConfig = dbConfig[key];
      if (contextConfig) {
        config[key] = {
          table: contextConfig.table ?? false,
          modal: contextConfig.modal ?? false,
          csv: contextConfig.csv ?? false,
          pdf: contextConfig.pdf ?? false,
        };
      }
    });
    return config;
  }, []);

  // Build dynamic field list from table columns
  const dynamicFields = useMemo(() => {
    // Filter out id and user_id as they're system fields
    return tableColumns.filter(col => col !== 'id' && col !== 'user_id');
  }, [tableColumns]);

  // Sync selectedTable with contextTableName when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTable(contextTableName);
    }
  }, [isOpen, contextTableName]);

  // Fetch columns for the selected table
  useEffect(() => {
    const loadColumns = async () => {
      setLoadingColumns(true);
      try {
        const columns = await fetchTableColumns(selectedTable);
        setTableColumns(columns);
      } catch (error) {
        console.error('Error fetching columns:', error);
        setTableColumns([]);
      } finally {
        setLoadingColumns(false);
      }
    };

    if (isOpen && selectedTable) {
      loadColumns();
    }
  }, [isOpen, selectedTable]);

  // Fetch visibility config directly for selected table (not from context)
  useEffect(() => {
    const loadConfig = async () => {
      if (!isOpen || !selectedTable || dynamicFields.length === 0) return;
      
      try {
        const { data, error } = await getTableFieldVisibilityConfig(selectedTable);
        
        if (error) {
          console.error('Error loading visibility config:', error);
          // Use default config on error
          const defaultConfig: Record<string, Record<VisibilityContext, boolean>> = {};
          dynamicFields.forEach(field => {
            defaultConfig[field] = { ...DEFAULT_FIELD_VISIBILITY };
          });
          setLocalConfig(defaultConfig);
        } else if (data) {
          // Merge with defaults for fields that don't exist in config
          const loadedConfig = convertToLocalConfig(data);
          const mergedConfig: Record<string, Record<VisibilityContext, boolean>> = {};
          dynamicFields.forEach(field => {
            if (loadedConfig[field]) {
              mergedConfig[field] = loadedConfig[field];
            } else {
              mergedConfig[field] = { ...DEFAULT_FIELD_VISIBILITY };
            }
          });
          setLocalConfig(mergedConfig);
        } else {
          // No config found, use defaults
          const defaultConfig: Record<string, Record<VisibilityContext, boolean>> = {};
          dynamicFields.forEach(field => {
            defaultConfig[field] = { ...DEFAULT_FIELD_VISIBILITY };
          });
          setLocalConfig(defaultConfig);
        }
      } catch (error) {
        console.error('Error loading visibility config:', error);
      }
    };

    loadConfig();
  }, [isOpen, selectedTable, dynamicFields, convertToLocalConfig]);

  const handleFieldChange = (fieldKey: string, context: VisibilityContext, checked: boolean) => {
    setLocalConfig(prev => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey],
        table: prev[fieldKey]?.table ?? true,
        modal: prev[fieldKey]?.modal ?? true,
        csv: prev[fieldKey]?.csv ?? true,
        pdf: prev[fieldKey]?.pdf ?? true,
        [context]: checked,
      },
    }));
    setHasChanges(true);
  };

  const handleContextToggle = (context: VisibilityContext, checked: boolean) => {
    setLocalConfig(prev => {
      const newConfig = { ...prev };
      Object.keys(newConfig).forEach(key => {
        newConfig[key] = {
          ...newConfig[key],
          table: newConfig[key]?.table ?? true,
          modal: newConfig[key]?.modal ?? true,
          csv: newConfig[key]?.csv ?? true,
          pdf: newConfig[key]?.pdf ?? true,
          [context]: checked,
        };
      });
      return newConfig;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Save directly to the selected table's visibility config
    try {
      // Convert local config to database format
      const dbConfig: FieldVisibilityConfig = {};
      Object.entries(localConfig).forEach(([fieldKey, contexts]) => {
        dbConfig[fieldKey] = {
          table: contexts.table ?? false,
          modal: contexts.modal ?? false,
          csv: contexts.csv ?? false,
          pdf: contexts.pdf ?? false,
        };
      });
      
      await updateTableFieldVisibilityConfig(selectedTable, dbConfig);
      
      // Update the global context if we're editing the current table
      if (selectedTable === contextTableName) {
        const updates: { fieldKey: string; context: VisibilityContext; visible: boolean }[] = [];
        Object.entries(localConfig).forEach(([fieldKey, contexts]) => {
          Object.entries(contexts).forEach(([context, visible]) => {
            updates.push({
              fieldKey,
              context: context as VisibilityContext,
              visible,
            });
          });
        });
        if (updates.length > 0) {
          updateFieldVisibilityBatch(updates);
        }
      }
      
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving visibility config:', error);
    }
  };

  const handleCancel = () => {
    // Reset to defaults
    const config: Record<string, Record<VisibilityContext, boolean>> = {};
    dynamicFields.forEach(field => {
      config[field] = { ...DEFAULT_FIELD_VISIBILITY };
    });
    setLocalConfig(config);
    setSelectedTable(contextTableName);
    setHasChanges(false);
    onClose();
  };

  const handleReset = () => {
    const config: Record<string, Record<VisibilityContext, boolean>> = {};
    dynamicFields.forEach(field => {
      config[field] = { ...DEFAULT_FIELD_VISIBILITY };
    });
    setLocalConfig(config);
    setHasChanges(true);
  };

  const isAllChecked = (contextKey: VisibilityContext) => {
    if (Object.keys(localConfig).length === 0) return false;
    return Object.keys(localConfig).every(key =>
      localConfig[key]?.[contextKey] ?? false
    );
  };

  const handleContextHeaderClick = (contextKey: VisibilityContext) => {
    const allChecked = isAllChecked(contextKey);
    handleContextToggle(contextKey, !allChecked);
  };

  // Format field name for display ( snake_case -> Title Case )
  const formatFieldName = (field: string): string => {
    return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="!w-[900px] !max-w-[90vw] h-[85vh] flex flex-col p-0 overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 p-6 pb-4 border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Settings className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold">Field Visibility Settings</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Control which fields are shown in different views and exports
              </p>
            </div>
            {/* Table Selector */}
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              <select
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                className="px-3 py-2 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {availableTables.map((table) => (
                  <option key={table.name} value={table.name}>
                    {table.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Context Headers */}
        <div className="flex-shrink-0 px-4 py-3 border-b bg-muted/10">
          <div className="flex items-center gap-4">
            <div className="w-48 shrink-0 pl-2">
              <span className="text-sm font-medium text-muted-foreground">Field</span>
            </div>
            {CONTEXTS.map(context => (
              <div
                key={context.key}
                className="flex-1 flex flex-col items-center cursor-pointer select-none"
                onClick={() => handleContextHeaderClick(context.key)}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isAllChecked(context.key)}
                    onChange={(e) => handleContextToggle(context.key, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-300 cursor-pointer w-5 h-5"
                  />
                  <span className="text-sm font-medium">{context.label}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 text-center">{context.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Fields List - Dynamic based on table columns */}
        <div className="flex-1 overflow-y-auto flex-1 px-4 py-2 scrollbar-modal">
          {loadingColumns ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading columns...</span>
            </div>
          ) : dynamicFields.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-muted-foreground">No columns found for this table</span>
            </div>
          ) : (
            <div className="space-y-1">
              {dynamicFields.map(field => (
                <div
                  key={field}
                  className="flex items-center gap-4 py-2 px-2 hover:bg-muted/50 rounded transition-colors"
                >
                  <div className="w-48 shrink-0 pl-2">
                    <span className="text-sm font-medium">{formatFieldName(field)}</span>
                  </div>
                  {CONTEXTS.map(context => (
                    <div key={context.key} className="flex-1 flex justify-center">
                      <input
                        type="checkbox"
                        checked={localConfig[field]?.[context.key] ?? false}
                        onChange={(e) => handleFieldChange(field, context.key, e.target.checked)}
                        className="rounded border-gray-300 cursor-pointer w-5 h-5"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-6 pt-4 border-t bg-muted/20">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Reset to Defaults
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} className="gap-2">
                <Check className="h-4 w-4" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
